import React, { useRef, useState } from 'react';
import { TextField } from '@material-ui/core';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import { useTranslation } from 'react-i18next';
import { newRequestKey, PaymentRequest, renewPaymentRequest } from 'api/paymentRequestsApi';
import LineEditor from './LineEditor';
import { emptyForm, formToInput, LineDraft } from './paymentRequestForm';
import { useFieldIds } from './fieldIds';

const toDraft = (request: PaymentRequest): LineDraft[] =>
  request.items.lines.map((line) => ({
    description: line.description,
    quantity: String(line.quantity),
    unitPrice: (line.unitPriceCents / 100).toFixed(2).replace('.', ','),
    vatRate: String(Number(line.vatRate)),
  }));

/**
 * "Request again": the request keeps its identity; after the open attempt is
 * settled a new link version (and optionally a new position version) replaces
 * the old link, which stays invalid.
 */
const RenewModal: React.FC<{
  restaurantId: string;
  request: PaymentRequest;
  onClose: () => void;
  onDone: (request: PaymentRequest) => void;
}> = ({ restaurantId, request, onClose, onDone }) => {
  const { t } = useTranslation('reservations');
  const fieldId = useFieldIds('renew');
  const [renewKey] = useState(newRequestKey);
  const [expiresAt, setExpiresAt] = useState('');
  const [editLines, setEditLines] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>(() => toDraft(request));
  const [channel, setChannel] = useState<'' | 'email' | 'sms'>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // Two clicks can land before `busy` re-renders; the ref closes that gap.
  const submitting = useRef(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (submitting.current) return;
    const parsed = editLines
      ? formToInput({ ...emptyForm(), purpose: request.purpose, mode: 'lines', lines }, renewKey)
      : null;
    if (parsed && !parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    const expiry = expiresAt ? Date.parse(expiresAt) : null;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      onDone(
        await renewPaymentRequest(restaurantId, request.id, {
          renewKey,
          expiresAt: expiry,
          ...(parsed?.ok ? { lines: parsed.input.lines } : {}),
          ...(channel ? { channel } : {}),
        })
      );
    } catch (err: any) {
      setError(err.message);
      setBusy(false);
      submitting.current = false;
    }
  };

  return (
    <Modal open title={t('Request payment again')} maxWidth="md" fullWidth onClose={busy ? undefined : onClose}>
      <Box style={{ display: 'grid', gap: 12 }}>
        <Typography variant="text-3" block translation={null}>
          {t('An open checkout is cancelled first. The old link stays invalid; the request and invoice stay the same.')}
        </Typography>
        <TextField
          id={fieldId('valid-until')}
          size="small"
          variant="outlined"
          type="datetime-local"
          label={t('Valid until (optional)')}
          InputLabelProps={{ shrink: true }}
          helperText={t('Default: 24 hours, at most until the end of the reservation')}
          value={expiresAt}
          onChange={(event) => setExpiresAt(event.target.value)}
        />
        <Box>
          <Button variant="outlined" translation={null} onClick={() => setEditLines((value) => !value)}>
            {editLines ? t('Keep positions') : t('Change positions')}
          </Button>
        </Box>
        {editLines && <LineEditor lines={lines} errors={errors} disabled={busy} onChange={setLines} />}
        <TextField
          id={fieldId('channel')}
          select
          SelectProps={{ native: true }}
          size="small"
          variant="outlined"
          label={t('Send via')}
          value={channel}
          onChange={(event) => setChannel(event.target.value as any)}
        >
          <option value="">{t('Only create (send later)')}</option>
          <option value="email">{t('Email')}</option>
          <option value="sms">SMS</option>
        </TextField>
        {error && (
          <Typography color="error" block translation={null}>
            <span role="alert">{error}</span>
          </Typography>
        )}
        <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="outlined" translation={null} disabled={busy} onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" translation={null} loading={busy} disabled={busy} onClick={submit}>
            {t('Create new link')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RenewModal;
